import { useSelectedLanguage } from '~/composables/use-selected-language'
import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { computed, ref, type Ref, watch } from 'vue'
import type {
    AttachmentInput,
    EnrollmentConstraintNewCustomerInput
} from '~/resources/graphql/graphql'
import type { EnrollmentInputArray } from '~/composables/enrollment-input'
import { useRpQuery } from '~/composables/graphql'
import { useQuery } from '@tanstack/vue-query'
import { useLayoutMode } from '~/composables/use-layout-mode'

export type EnrollmentAttachmentData = {
    enrollmentTypeId: string
    enrollmentTypeSequence: number
    attachmentTypeId: string
    attachmentTypeName: string
    file: File
}

export type AttachmentRequirement = {
    attachmentTypeId: string
    attachmentTypeName: string
    attachmentResult: {
        title: string
        valid: boolean | null
        expiresAt: string | null
    } | null
}

export const EnrollmentConstraintPlanContractFragment = graphql(`
    fragment EnrollmentConstraintPlanContractFragment on PlanContract {
        id
        enrollmentConstraints {
            ...EnrollmentConstraintEnrollmentConstraintsFragment
        }
    }
`)

const EnrollmentConstraintEnrollmentConstraintsFragment = graphql(`
    fragment EnrollmentConstraintEnrollmentConstraintsFragment on EnrollmentConstraints {
        enrollmentType {
            id
            enrollmentConstraintEnforced
            enrollmentConstraintTranslatedMessage(language: $language)
        }
        results {
            customer {
                __typename
                ... on Customer {
                    id
                }
                ... on ConstraintNewCustomer {
                    key
                }
            }
            valid
            attachment {
                attachmentType {
                    id
                    name
                }
                attachmentResult {
                    title
                    valid
                    expiresAt
                }
            }
        }
    }
`)

export function useEnrollmentConstraint(
    enrollmentInput: Ref<EnrollmentInputArray>,
    contract: Ref<FragmentType<typeof EnrollmentConstraintPlanContractFragment>>
) {
    const { isKiosk } = useLayoutMode()

    const contractData = computed(() =>
        getFragmentData(
            EnrollmentConstraintPlanContractFragment,
            contract.value
        )
    )

    const newCustomerFilter = computed(() => {
        return enrollmentInput.value.flatMap(input => {
            return input.enrollments
                .map(enrollment => {
                    if (
                        (enrollment.invalid &&
                            !enrollment.invalidFromConstraint) ||
                        enrollment.data === null ||
                        enrollment.data.type === 'existing'
                    ) {
                        return null
                    }

                    return {
                        key: enrollment.data.key,
                        firstName: enrollment.data.firstName,
                        middleName: enrollment.data.middleName,
                        lastName: enrollment.data.lastName,
                        dateOfBirth: enrollment.data.dateOfBirth
                    }
                })
                .filter(
                    value => value !== null
                ) as EnrollmentConstraintNewCustomerInput[]
        })
    })

    const existingCustomerIds = computed(() => {
        const customerIdsWithInitialConstraints = new Set<string>()

        contractData.value.enrollmentConstraints.forEach(
            enrollmentConstraint => {
                const constraint = getFragmentData(
                    EnrollmentConstraintEnrollmentConstraintsFragment,
                    enrollmentConstraint
                )

                constraint.results.forEach(result => {
                    if (result.customer.__typename === 'Customer') {
                        customerIdsWithInitialConstraints.add(
                            result.customer.id
                        )
                    }
                })
            }
        )

        return enrollmentInput.value.flatMap(input => {
            return input.enrollments.flatMap(enrollment => {
                if (
                    enrollment.data === null ||
                    enrollment.data.type !== 'existing' ||
                    enrollment.data.id === '' ||
                    customerIdsWithInitialConstraints.has(enrollment.data.id)
                ) {
                    return []
                }

                return [enrollment.data.id]
            })
        })
    })

    const rpQuery = useRpQuery()
    const selectedLanguage = useSelectedLanguage()
    const {
        data: newCustomerConstraints,
        isFetching: isFetchingNewCustomerConstraints
    } = useQuery({
        queryKey: [
            'ParticipantDetailsNewCustomerConstraintQuery',
            newCustomerFilter,
            selectedLanguage
        ],
        queryFn: async () => {
            if (newCustomerFilter.value.length === 0) {
                return []
            }

            const response = await rpQuery(
                graphql(`
                    query ParticipantDetailsNewCustomerConstraintQuery(
                        $id: ID!
                        $filter: EnrollmentConstraintNewCustomerFilter!
                        $language: Language!
                    ) {
                        node(id: $id) {
                            __typename
                            ... on PlanContract {
                                enrollmentConstraintsNewCustomers(
                                    filter: $filter
                                ) {
                                    ...EnrollmentConstraintEnrollmentConstraintsFragment
                                }
                            }
                        }
                    }
                `),
                {
                    id: contractData.value.id,
                    filter: {
                        customers: newCustomerFilter.value
                    },
                    language: selectedLanguage.value
                }
            )

            if (
                response.node !== null &&
                response.node.__typename === 'PlanContract' &&
                response.node.enrollmentConstraintsNewCustomers
            ) {
                return response.node.enrollmentConstraintsNewCustomers
            }

            return []
        }
    })

    const {
        data: existingCustomerConstraints,
        isFetching: isFetchingExistingCustomerConstraints
    } = useQuery({
        queryKey: [
            'ParticipantDetailsExistingCustomerConstraintQuery',
            existingCustomerIds,
            selectedLanguage
        ],
        queryFn: async () => {
            if (existingCustomerIds.value.length === 0) {
                return []
            }

            // Re-read constraints after a booking-ID lookup adds a saved participant.
            // The contract fragment was loaded before that relationship existed.
            const response = await rpQuery(
                graphql(`
                    query ParticipantDetailsExistingCustomerConstraintQuery(
                        $id: ID!
                        $language: Language!
                    ) {
                        node(id: $id) {
                            __typename
                            ... on PlanContract {
                                enrollmentConstraints {
                                    ...EnrollmentConstraintEnrollmentConstraintsFragment
                                }
                            }
                        }
                    }
                `),
                {
                    id: contractData.value.id,
                    language: selectedLanguage.value
                }
            )

            if (
                response.node !== null &&
                response.node.__typename === 'PlanContract'
            ) {
                return response.node.enrollmentConstraints
            }

            return []
        }
    })

    const constraintsAreFetching = computed(() => {
        return (
            isFetchingNewCustomerConstraints.value ||
            isFetchingExistingCustomerConstraints.value
        )
    })

    const combinedEnrollmentConstraints = computed(() => {
        return [
            ...contractData.value.enrollmentConstraints,
            ...(existingCustomerConstraints.value ?? []),
            ...(newCustomerConstraints.value ?? [])
        ]
    })

    const enrollmentConstraintSegmentLookup = computed(() => {
        const map = new Map<string, Map<string, boolean>>()

        combinedEnrollmentConstraints.value.forEach(enrollmentConstraint => {
            const constraint = getFragmentData(
                EnrollmentConstraintEnrollmentConstraintsFragment,
                enrollmentConstraint
            )

            const enrollmentTypeMap =
                map.get(constraint.enrollmentType.id) ??
                new Map<string, boolean>()

            constraint.results.forEach(result => {
                // Skip anybody who can use an attachment to satisfy
                if (result.attachment !== null) {
                    return
                }

                // Skip anybody who does not have a customer segment to match on
                if (result.valid === null) {
                    return
                }

                const key =
                    result.customer.__typename === 'Customer'
                        ? result.customer.id
                        : result.customer.key

                enrollmentTypeMap.set(key, result.valid)
            })

            map.set(constraint.enrollmentType.id, enrollmentTypeMap)
        })

        return map
    })

    const enrollmentConstraintAttachmentLookup = computed(() => {
        const map = new Map<string, Map<string, AttachmentRequirement>>()

        combinedEnrollmentConstraints.value.forEach(enrollmentConstraint => {
            const constraint = getFragmentData(
                EnrollmentConstraintEnrollmentConstraintsFragment,
                enrollmentConstraint
            )

            const enrollmentTypeMap =
                map.get(constraint.enrollmentType.id) ??
                new Map<string, AttachmentRequirement>()

            constraint.results.forEach(result => {
                // Skip anybody who matches on customer segment
                if (result.valid) {
                    return
                }

                // Skip anybody who doesn't need an attachment
                if (result.attachment === null) {
                    return
                }

                const key =
                    result.customer.__typename === 'Customer'
                        ? result.customer.id
                        : result.customer.key

                if (result.attachment.attachmentResult === null) {
                    enrollmentTypeMap.set(key, {
                        attachmentTypeId: result.attachment.attachmentType.id,
                        attachmentTypeName:
                            result.attachment.attachmentType.name,
                        attachmentResult: null
                    })
                } else {
                    enrollmentTypeMap.set(key, {
                        attachmentTypeId: result.attachment.attachmentType.id,
                        attachmentTypeName:
                            result.attachment.attachmentType.name,
                        attachmentResult: {
                            title: result.attachment.attachmentResult.title,
                            valid: result.attachment.attachmentResult.valid,
                            expiresAt:
                                result.attachment.attachmentResult.expiresAt
                        }
                    })
                }
            })

            map.set(constraint.enrollmentType.id, enrollmentTypeMap)
        })

        return map
    })

    const enrollmentConstraintConfigLookup = computed(() => {
        const map = new Map<
            string,
            {
                required: boolean
                message: string
            }
        >()

        combinedEnrollmentConstraints.value.forEach(enrollmentConstraint => {
            const constraint = getFragmentData(
                EnrollmentConstraintEnrollmentConstraintsFragment,
                enrollmentConstraint
            )

            // Technically this will override, but we don't really care
            map.set(constraint.enrollmentType.id, {
                required:
                    constraint.enrollmentType.enrollmentConstraintEnforced,
                message:
                    constraint.enrollmentType
                        .enrollmentConstraintTranslatedMessage
            })
        })

        return map
    })

    const enrollmentTypeAttachments = ref<EnrollmentAttachmentData[]>([])

    const attachmentsAreInvalid = computed(() => {
        // Check every enrollment slot
        return !enrollmentInput.value.every(enrollmentGroup => {
            return enrollmentGroup.enrollments.every(
                (enrollment, enrollmentIndex) => {
                    if (enrollment.data === null) {
                        return true
                    }

                    let key
                    // Actual customer record
                    if (enrollment.data.type === 'existing') {
                        key = enrollment.data.id
                    } else {
                        key = enrollment.data.key
                    }

                    const attachmentRequirement =
                        enrollmentConstraintAttachmentLookup.value
                            .get(enrollmentGroup.enrollmentType.id)
                            ?.get(key)

                    // Attachment requirement found
                    if (attachmentRequirement !== undefined) {
                        // Not actually required
                        const details =
                            enrollmentConstraintConfigLookup.value.get(
                                enrollmentGroup.enrollmentType.id
                            )
                        if (details === undefined) {
                            throw new Error('Unable to find enrollment specs')
                        }

                        if (details.required === false) {
                            return true
                        }

                        // Already has one on the server - valid
                        if (attachmentRequirement.attachmentResult !== null) {
                            return true
                        } else {
                            // Try to find in pending uploads
                            const uploadingAttachment =
                                enrollmentTypeAttachments.value.find(
                                    attachment =>
                                        attachment.enrollmentTypeId ===
                                            enrollmentGroup.enrollmentType.id &&
                                        attachment.enrollmentTypeSequence ===
                                            enrollmentIndex + 1
                                )

                            // Have it locally in browser to upload - return true
                            if (uploadingAttachment !== undefined) {
                                return true
                            } else {
                                if (isKiosk.value) {
                                    return true
                                }

                                return false
                            }
                        }
                    }

                    return true
                }
            )
        })
    })

    function getAttachmentInput(): AttachmentInput[] {
        return enrollmentTypeAttachments.value.map(attachment => {
            return {
                enrollmentSlot: {
                    enrollmentTypeId: attachment.enrollmentTypeId,
                    enrollmentIndex: attachment.enrollmentTypeSequence - 1
                },
                attachmentTypeId: attachment.attachmentTypeId,
                file: attachment.file
            }
        })
    }

    function resetAttachmentUploads(): void {
        enrollmentTypeAttachments.value = []
    }

    // If the enrollment changes from needing an attachment to not, here is where we clear it
    watch(enrollmentInput, newValue => {
        newValue.forEach(enrollmentGroup => {
            enrollmentGroup.enrollments.forEach(
                (enrollment, enrollmentIndex) => {
                    // Don't care about anonymous enrollments
                    if (enrollment.data === null) {
                        return
                    }

                    let key
                    // Actual customer record
                    if (enrollment.data.type === 'existing') {
                        key = enrollment.data.id
                    } else {
                        key = enrollment.data.key
                    }

                    const attachmentRequirement =
                        enrollmentConstraintAttachmentLookup.value
                            .get(enrollmentGroup.enrollmentType.id)
                            ?.get(key)

                    // Attachment requirement not found - clear attachment
                    if (attachmentRequirement === undefined) {
                        enrollmentTypeAttachments.value =
                            enrollmentTypeAttachments.value.filter(
                                attachment =>
                                    attachment.enrollmentTypeId !==
                                        enrollmentGroup.enrollmentType.id ||
                                    attachment.enrollmentTypeSequence !==
                                        enrollmentIndex + 1
                            )
                    }
                }
            )
        })
    })

    function handleFileUpload(
        enrollmentTypeId: string,
        enrollmentTypeSequence: number,
        attachmentRequirement: AttachmentRequirement,
        file: File
    ) {
        enrollmentTypeAttachments.value = [
            ...enrollmentTypeAttachments.value,
            {
                enrollmentTypeId,
                enrollmentTypeSequence,
                attachmentTypeId: attachmentRequirement.attachmentTypeId,
                attachmentTypeName: attachmentRequirement.attachmentTypeName,
                file
            }
        ]
    }

    function handleReplaceFileUpload(
        enrollmentTypeId: string,
        enrollmentTypeSequence: number,
        attachmentRequirement: AttachmentRequirement,
        file: File
    ) {
        enrollmentTypeAttachments.value = [
            ...enrollmentTypeAttachments.value.filter(
                attachment =>
                    !(
                        attachment.enrollmentTypeId === enrollmentTypeId &&
                        attachment.enrollmentTypeSequence ===
                            enrollmentTypeSequence
                    )
            ),
            {
                enrollmentTypeId,
                enrollmentTypeSequence,
                attachmentTypeId: attachmentRequirement.attachmentTypeId,
                attachmentTypeName: attachmentRequirement.attachmentTypeName,
                file
            }
        ]
    }

    return {
        constraintsAreFetching,
        enrollmentTypeAttachments,
        attachmentsAreInvalid,
        getAttachmentInput,
        resetAttachmentUploads,

        enrollmentConstraints: {
            enrollmentConstraintSegmentLookup,
            enrollmentConstraintAttachmentLookup,
            enrollmentConstraintConfigLookup,
            enrollmentTypeAttachments,
            handleFileUpload,
            handleReplaceFileUpload
        }
    }
}

export type EnrollmentConstraints = ReturnType<
    typeof useEnrollmentConstraint
>['enrollmentConstraints']
